import { HsTrack } from '@hs/hs-js-maat-base';
import { HsTrackEnvEnum as Env, HsTrackPlatformEnum } from '@hs/hs-js-maat-types';
import { WebAutoRun, WebExtension, WebNetwork, WebStorage } from '@hs/hs-js-maat-plugin-h5';
import { isMobile, parseUrlParams } from '~/utils/common';
import { safeJsonStringify } from '~/utils/safeJsonStringify';
import publicMaatData from './publicMaatData';

export function uuid() {
  let d = new Date().getTime();

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);

    // eslint-disable-next-line no-bitwise
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

const FILTER_ERROR_LIST = [
  'CountryList_user_dismissed', // 国家列表用户取消,最小改动直接保持之前的行为，避免上报错误
  'userFontsizeChanged',
  '__TG_GET_PAGE_PERFORMANCE__',
  'userFontsizeChanged',
  'downProgCallback',
  'NadJsControl',
  'transaction',
  'evaluating',
  'indexeddb',
  'idbdatabase',
  'idbfactory',
  'unexpected token .',
  'hard navigate to the same',
  'cancel rendering route',
  'maximum call stack',
  'xbrowser',
  'vivonewsdetailpage',
  'insertstyle',
  'onandroidback',
  'x5onskinswitch',
  'lidnotifyid',
  'viewwillappear',
  'sdkevent',
  'webviewjavascriptbridge',
  'getreadmoderender',
  'getfeedexposedinfo',
  'tencentnewscallback',
  'swbrowser',
  'processrandomselector',
  'runCustomize',
  'Java object is gone',
  'TencentNewsCallback',
  'tad_get_back_gesture_enabled',
  'Cannot set property',
  `Can't find variable`,
  'window.bannerNight',
  'insertHotWords is not defined',
  'onWebWindowScrollY',
  'Loading chunk',
  'attempted to hard navigate',
  'Unexpected token',
];

// 1.maat 埋点
const maatBaseEnv = {
  dev: Env.BETA,
  test: Env.BETA,
  beta: Env.BETA,
  prod: Env.PROD,
}[process?.env?.baseEnv || 'prod'];

const onErrorWrapper = (errorType: string, errorMsg: any) => {
  //  GTM脚本、第三方 SDK 的空 rejection，过滤
  if (errorType === 'unhandledrejection' && !errorMsg) {
    return false;
  }
  // 直接返回 走sdk逻辑
  if (!errorMsg) {
    return true;
  }
  // unhandledrejection 事件中 SDK 传入的是原始 reason（可能是 Error 对象），
  // JSON.stringify(Error) 会得到 "{}"，导致过滤失效，需优先取 message
  if (errorMsg instanceof Error) {
    errorMsg = errorMsg.message || errorMsg.stack || String(errorMsg);
  } else if (typeof errorMsg !== 'string') {
    errorMsg = safeJsonStringify(errorMsg, '[unserializable_error_reason]');
  }
  const normalizedErrorMsg = String(errorMsg).toLowerCase();
  switch (errorType) {
    case 'error':
    case 'unhandledrejection':
    case 'onerror':
    default:
      if (FILTER_ERROR_LIST.find((str) => normalizedErrorMsg.includes(str.toLowerCase()))) {
        return false;
      }
  }
  return true;
};

type ErrorData = {
  name: string;
  title: string;
  message: string;
  content: string;
} & Record<string, string | number | boolean>;

class MaatPro {
  private hsMaat: any;

  private _distinct_id: string;

  constructor() {
    this._distinct_id = '';
    this.hsMaat = this.getHsMaat();
  }

  getHsMaat() {
    if (this.hsMaat) return this.hsMaat;

    if (typeof window === 'undefined') {
      return null;
    }

    return new HsTrack({
      env: maatBaseEnv as any,
      platform: HsTrackPlatformEnum.WEB,
      // hsUserId: "hs-jiemm",
      // fjUserId: "fj-jiemm",
      // tempUserId: "",
      tempUserId: this.getDistinctId(),
      appName: 'fjkt-fe-hera',
      storageEngine: new WebStorage(),
      networkEngine: new WebNetwork(),
      extension: new WebExtension(),
      autoRun: new WebAutoRun({
        autoBaseError: true,
        onErrorWrapper,
      }),
      // getPageViewPageId: (path) => {
      //   return undefined;
      // },
    });
  }

  // 获取公共参数
  getPublicData() {
    return {
      ...publicMaatData.getData(),
      page_platform: isMobile() ? 'h5' : 'pc',
      requirement_name: '1.0.0',
      page_name: window.location.pathname,
      distinct_id: this.getDistinctId(),
      user_language: navigator.language,
      page_full_url: window.location.href, // 页面完整url
    };
  }

  /**
   * 获取用户实例标记
   * @returns uuid {string}
   *
   */
  getDistinctId() {
    if (this._distinct_id) {
      return this._distinct_id;
    }

    let landPageUuid;
    let hrefParams: Record<any, any> = {};
    if (typeof window !== 'undefined') {
      hrefParams = parseUrlParams(window.location.href);
    }

    if (hrefParams?.distinct_id) {
      landPageUuid = hrefParams.distinct_id;
    } else if (typeof localStorage !== 'undefined') {
      landPageUuid = localStorage.getItem('langpage_uuid');
    }

    if (landPageUuid) {
      this._distinct_id = landPageUuid;
      return this._distinct_id;
    }

    landPageUuid = uuid();
    if (typeof localStorage !== 'undefined') {
      localStorage?.setItem('langpage_uuid', landPageUuid);
    }
    this._distinct_id = landPageUuid;
    return this._distinct_id;
  }

  /**
   * 进入
   * @param actionName {string} 事件名称
   * @param data {Record<string,string>} 参数
   * @param dataType {DataType} 业务类型枚举
   * */
  $scroll(actionName: string, data?: Record<string, string | number>) {
    this.getHsMaat()?.onExposure({
      pageId: actionName, // 在埋点平台注册页面id
      // pageValue?: string; // 页面属性
      // eventElement?: string; // 元素
      // targetName?: string; // 操作对象
      // targetValue?: string;  // 对象属性
      sendImmediately: true, // 是否立即发送
      content: {
        ...this.getPublicData(),
        ...data,
        event_type: 'scroll',
      },
    });
  }

  /**
   * 点击
   * @param actionName {string} 事件名称
   * @param data {Record<string,string>} 参数
   * @param dataType {DataType} 业务类型枚举
   * */
  $click(actionName: string, data?: Record<string, string | number | boolean>, pageValue?: string) {
    this.getHsMaat()?.onClick({
      pageId: actionName, // 在埋点平台注册页面id
      pageValue, // 页面属性
      // eventElement?: string; // 元素
      // targetName?: string; // 操作对象
      // targetValue?: string;  // 对象属性
      sendImmediately: true, // 是否立即发送
      content: {
        ...this.getPublicData(),
        ...data,
      },
    });
  }

  // /**
  //  * 分享
  //  * @param actionName {string} 事件名称
  //  * @param data {Record<string,string>} 参数
  //  * @param dataType {DataType} 业务类型枚举
  //  * */
  // $share(actionName: string, data?: Record<string, string>, dataType?: DataType) {
  //   this.getHsMaat()?.track(
  //     actionName,
  //     {
  //       ...this.getPublicData(),
  //       ...data,
  //       event_type: 'share',
  //     },
  //     dataType
  //   );
  // }

  // /**
  //  * 长按
  //  * @param actionName {string} 事件名称
  //  * @param data {Record<string,string>} 参数
  //  * @param dataType {DataType} 业务类型枚举
  //  * */
  // $longPress(actionName: string, data?: Record<string, string>, dataType?: DataType) {
  //   this.getHsMaat()?.track(
  //     actionName,
  //     {
  //       ...this.getPublicData(),
  //       ...data,
  //       event_type: 'longPress',
  //     },
  //     dataType
  //   );
  // }

  /**
   * 心跳
   * @param actionName {string} 事件名称
   * @param data {Record<string,string>} 参数
   * @param dataType {DataType} 业务类型枚举
   * */
  $heartBeat(actionName: string, data?: Record<string, string>) {
    this.getHsMaat()?.onExposure({
      pageId: actionName, // 在埋点平台注册页面id
      // pageValue?: string; // 页面属性
      // eventElement?: string; // 元素
      // targetName?: string; // 操作对象
      // targetValue?: string;  // 对象属性
      sendImmediately: true, // 是否立即发送
      content: {
        ...this.getPublicData(),
        ...data,
        event_type: 'heartBeat',
      },
    });
  }

  /**
   * 动作
   * @param actionName {string} 事件名称
   * @param data {Record<string,string>} 参数
   * @param dataType {DataType} 业务类型枚举
   * */
  $action(actionName: string, data?: Record<string, string | number | boolean>, pageValue?: string) {
    this.getHsMaat()?.onExposure({
      pageId: actionName, // 在埋点平台注册页面id
      pageValue, // 页面属性
      // eventElement?: string; // 元素
      // targetName?: string; // 操作对象
      // targetValue?: string;  // 对象属性
      sendImmediately: true, // 是否立即发送
      content: {
        ...this.getPublicData(),
        ...data,
      },
    });
  }

  /**
   * 页面浏览
   * @param data {Record<string,string>} 参数
   * */
  $MPViewScreen(
    data = {
      element_type: 'page',
      element_name: '页面浏览',
      requirement_name: '打开页面',
    }
  ) {
    this.getHsMaat()?.onPageView({
      pageId: 'h5_viewScreen', // 在埋点平台注册页面id
      // pageValue?: string; // 页面属性
      // eventElement?: string; // 元素
      // targetName?: string; // 操作对象
      // targetValue?: string;  // 对象属性
      sendImmediately: true, // 是否立即发送
      content: {
        ...this.getPublicData(),
        ...data,
      },
    });
  }

  /**
   * 退出页面
   * */
  $exitPage() {
    this.getHsMaat()?.onClick({
      pageId: 'h5_exitPage', // 在埋点平台注册页面id
      // pageValue?: string; // 页面属性
      // eventElement?: string; // 元素
      // targetName?: string; // 操作对象
      // targetValue?: string;  // 对象属性
      sendImmediately: true, // 是否立即发送
      content: {
        ...this.getPublicData(),
        element_type: 'page',
        element_name: '退出页面浏览',
        requirement_name: '退出页面浏览',
        event_type: 'heartBeat',
      },
    });
  }

  /**
   * 页面心跳
   * */
  $heartBeatPage(data = {}) {
    this.$heartBeat('h5_heartBeatPage', {
      ...data,
      element_type: 'page',
      element_name: '页面浏览心跳',
      requirement_name: '页面浏览心跳',
    });
  }

  /**
   * 错误
   * @param actionName {string} 事件名称
   * @param data {Record<string,string>} 参数
   * @param dataType {DataType} 业务类型枚举
   * */
  $onError(data: ErrorData) {
    this.getHsMaat()?.onError({
      pageId: 'hera_landing_page_error', // 在埋点平台注册页面id
      pageValue: data.title, // 页面属性
      sendImmediately: true, // 是否立即发送
      eventElement: data.name,
      targetName: data.message,
      targetValue: data.content,
      content: {
        ...this.getPublicData(),
        ...data,
      },
    });
  }

  /**
   * 请求错误
   * @param actionName {string} 事件名称
   * @param data {Record<string,string>} 参数
   * @param dataType {DataType} 业务类型枚举
   * */
  $onRequestError(data: ErrorData) {
    this.getHsMaat()?.onRequest({
      pageId: 'hera_landing_page_error', // 在埋点平台注册页面id
      pageValue: data.title, // 页面属性
      sendImmediately: true, // 是否立即发送
      eventElement: data.name,
      targetName: data.message,
      targetValue: data.content,
      content: {
        ...this.getPublicData(),
        ...data,
      },
    });
  }
}

export default new MaatPro();
